import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Como a pessoa entrou no app — e se ela já viu o aviso de protótipo.
 *
 * ## O que cada campo resolve
 *
 * `hasSeenPrototypeNotice` é a flag `firstAccess` da RN-02: o pop-up de aviso
 * aparece **uma vez**, no primeiro acesso. Guardar isso em memória faria o aviso
 * voltar a cada refresh, que é o oposto de "primeiro acesso".
 *
 * `isGuest` registra a escolha de "Entrar como convidado" (RN-01). Sem ela, a
 * tela inicial reapareceria a cada abertura para quem decidiu navegar sem conta —
 * e a decisão de entrar como convidado é justamente a de não ser incomodado.
 *
 * ## Por que persistido, ao contrário do rascunho de cadastro
 *
 * Aqui não há nada sensível: são duas preferências de navegação. Persistir é o
 * que dá sentido a ambas.
 *
 * ⚠️ `isGuest` **não é autorização**. Ela não concede acesso a nada: as ações
 * restritas continuam exigindo sessão, e é o pop-up "Login necessário" (RN-23)
 * que aparece quando um convidado tenta uma delas. Tratá-la como permissão seria
 * confiar num booleano que o próprio usuário pode editar no `localStorage`.
 */

interface AppEntryState {
  hasSeenPrototypeNotice: boolean;
  isGuest: boolean;
  markPrototypeNoticeSeen: () => void;
  enterAsGuest: () => void;
  reset: () => void;
}

export const useAppEntryStore = create<AppEntryState>()(
  persist(
    (set) => ({
      hasSeenPrototypeNotice: false,
      isGuest: false,

      markPrototypeNoticeSeen: () => set({ hasSeenPrototypeNotice: true }),
      enterAsGuest: () => set({ isGuest: true }),
      // O aviso de protótipo não volta no logout: quem saiu da conta já o viu.
      reset: () => set({ isGuest: false }),
    }),
    { name: 'arcus:app-entry' },
  ),
);
